<template>
  <Dialog
    v-model="useDialogStore().fakturoidWorkspace.isOpen"
    size="small-hug"
    @open="onOpen"
    @close="onClose"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('fakturoid.workspace.title')"
        :show-close="true"
        @close="close"
      />
    </template>

    <div
      v-if="isLoading"
      v-loading="true"
      class="h-80 w-full"
    />
    <div v-else>
      <Form
        ref="formRef"
        :model="form"
        :rules="rules"
        @submit="onSubmit"
      >
        <div class="flex items-center justify-between rounded-lg border border-gray-200 p-3 mb-4">
          <div class="mr-4">
            <div class="text-sm font-medium">
              {{ useT('fakturoid.workspace.isEnabled') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ useT('fakturoid.workspace.isEnabled.description') }}
            </div>
          </div>
          <Switch
            :model-value="form.isEnabled"
            @update:model-value="(value) => form.isEnabled = value"
          />
        </div>

        <InputBlock
          :label="useT('fakturoid.workspace.clientId')"
          prop="clientId"
        >
          <TextInput
            v-model="form.clientId"
            name="clientId"
          />
          <div class="text-xs text-gray-500 mt-1 w-full">
            {{ useT('fakturoid.workspace.credentialsHint') }}
          </div>
        </InputBlock>

        <InputBlock
          :label="useT('fakturoid.workspace.clientSecret')"
          prop="clientSecret"
        >
          <TextInput
            v-model="form.clientSecret"
            name="clientSecret"
          />
        </InputBlock>

        <InputBlock
          :label="useT('fakturoid.workspace.accountSlug')"
          prop="accountSlug"
        >
          <TextInput
            v-model="form.accountSlug"
            name="accountSlug"
          />
          <div class="text-xs text-gray-500 mt-1 w-full">
            {{ useT('fakturoid.workspace.accountSlugHint') }}
          </div>
        </InputBlock>
      </Form>

      <div class="mt-6 pt-4 border-t border-gray-200">
        <h3 class="text-sm font-semibold mb-3">
          {{ useT('fakturoid.workspace.manualSteps') }}
        </h3>

        <div
          v-for="row in copyRows"
          :key="row.key"
          class="flex items-center gap-2 mb-3"
        >
          <div class="flex-1 min-w-0">
            <div class="text-xs font-medium text-gray-700">
              {{ row.label }}
            </div>
            <div class="text-xs font-mono text-gray-500 truncate">
              {{ row.value }}
            </div>
          </div>
          <Button
            type="regular-outline"
            size="small"
            :icon-left="copied[row.key] ? 'check' : 'clipboard'"
            @click="onCopy(row)"
          >
            {{ copied[row.key] ? useT('cliToken.copy.copied') : useT('cliToken.copy.label') }}
          </Button>
        </div>

        <ul class="list-disc list-inside text-xs text-gray-500 space-y-1">
          <li>{{ useT('fakturoid.workspace.step.webhook') }}</li>
          <li>{{ useT('fakturoid.workspace.step.exchangeRate') }}</li>
          <li>{{ useT('fakturoid.workspace.step.footer') }}</li>
          <li>{{ useT('fakturoid.workspace.step.language') }}</li>
        </ul>
      </div>
    </div>

    <template
      v-if="!isLoading"
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="useT('bcd15')"
        :text-cancel="useT('bcd205')"
        align="left"
        :loading="isSubmitting"
        @submit="formRef.onSubmit()"
        @cancel="useDialogStore().close('fakturoidWorkspace')"
      />
    </template>
  </Dialog>
</template>

<script setup>
// Refs
const form = ref({
  isEnabled: false,
  clientId: '',
  clientSecret: '',
  accountSlug: '',
});

const isLoading = ref(false);
const isSubmitting = ref(false);
const formRef = ref(null);
const webhookUrl = ref('');
const webhookToken = ref('');
const copied = reactive({ webhookUrl: false, webhookToken: false });

// Computed
const rules = computed(() => (form.value.isEnabled ? {
  clientId: [useFormRules().required],
  clientSecret: [useFormRules().required],
  accountSlug: [useFormRules().required],
} : {}));

const copyRows = computed(() => [
  { key: 'webhookUrl', label: useT('fakturoid.workspace.webhookUrl'), value: webhookUrl.value },
  { key: 'webhookToken', label: useT('fakturoid.workspace.webhookToken'), value: webhookToken.value },
]);

// Methods
const onOpen = async() => {
  isLoading.value = true;

  const settings = await useFakturoidStore().fetchWorkspaceSettings(useDialogStore().fakturoidWorkspace.workspaceDomain);

  if (!settings) {
    useDialogStore().close('fakturoidWorkspace');
    return;
  }

  form.value = {
    isEnabled: settings.isEnabled,
    clientId: settings.clientId,
    clientSecret: settings.clientSecret,
    accountSlug: settings.accountSlug,
  };
  webhookUrl.value = settings.webhookUrl;
  webhookToken.value = settings.webhookToken;

  isLoading.value = false;
};

const onClose = () => {
  // form is rebuilt by onOpen on every reopen; only the copied flags need resetting.
  copied.webhookUrl = false;
  copied.webhookToken = false;
};

const onCopy = async(row) => {
  try {
    await navigator.clipboard.writeText(row.value);
    copied[row.key] = true;
    setTimeout(() => { copied[row.key] = false; }, 1500);
  } catch (error) {
    useToast.error(useT('cliToken.toast.clipboardError'));
  }
};

const onSubmit = async() => {
  isSubmitting.value = true;

  const response = await useFakturoidStore().saveWorkspaceSettings({
    workspaceDomain: useDialogStore().fakturoidWorkspace.workspaceDomain,
    body: {
      isEnabled: form.value.isEnabled,
      clientId: form.value.clientId?.trim() || '',
      clientSecret: form.value.clientSecret?.trim() || '',
      accountSlug: form.value.accountSlug?.trim() || '',
    },
  });

  isSubmitting.value = false;

  if (response) {
    useToast(useT('fakturoid.workspace.saved'));
    useDialogStore().fakturoidWorkspace.onSubmit(response);
    useDialogStore().close('fakturoidWorkspace');
  }
};
</script>
